import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommentIcon from '@mui/icons-material/Comment';
import CreateIcon from '@mui/icons-material/Create';
import PendingIcon from '@mui/icons-material/Pending';
import PTOAnswerViewerModal from '@/components/PTOAnswerViewerModal';
import Button from '@/trio-ui/Button';
import Pagination from '@/trio-ui/Pagination';
import { Td, Th, Tr } from '@/trio-ui/Table';
import { SystemUser } from '@/types';
import { PTORequest, PTOStatus, PTOStatusLabel, PTOType, PTOTypeBalance } from '@/types/api';
import { formatDateInterval } from '@/utils/dates';
import { iconByPTOType, ptoTypeLabel } from '@/utils/pto';
import * as Styles from './MyTimeOffsPageContent.styles';
import EditTimeoffRequestModal from '../EditTimeoffRequestModal';
import IconButton from '../IconButton';
import RequestTimeOffModal from '../RequestTimeOffModal';

const iconByPTOStatus: { [status in PTOStatus]: JSX.Element } = {
  [PTOStatus.APPROVED]: <CheckCircleIcon className="!tw-w-3 !tw-h-3" style={{ fill: '#2AD062' }} />,
  [PTOStatus.DENIED]: <CancelIcon className="!tw-w-3 !tw-h-3" style={{ fill: '#EF5B5B' }} />,
  [PTOStatus.PENDING]: <PendingIcon className="!tw-w-3 !tw-h-3" style={{ fill: '#ABACAF' }} />,
};

export type Props = {
  balance: Partial<Record<PTOType, PTOTypeBalance>>;
  onRequestClick?: (type?: PTOType) => void;
  isLoadingPTOs?: boolean;
  ptos?: PTORequest[];
  onEditRequestClick?: (request: PTORequest) => void;
  lastPage?: number;
  currentPage: number;
  totalPTOs?: number;
  itemsPerPage: number;
  onPageChange: (value: number) => void;
  prefilledPTOType?: PTOType;
  editingRequest?: PTORequest;
  isRequestedModalOpen: boolean;
  onCloseModal: () => void;
  onPTOsUpdate?: () => void;
  adminMode?: boolean;
  profile?: SystemUser;
  isAdminEditOpen?: boolean;
  onAdminEdit?: (pto: PTORequest) => void;
  onAdminEditClose: () => void;
  isBalanceLoading?: boolean;
  onViewAnswerNoteClick?: (request: PTORequest) => void;
  showAnswerViewerModal?: boolean;
  onCloseAnswerViewerModal: () => void;
  answerViewerRequest?: PTORequest;
};

const MyTimeOffsPageContent = ({
  balance,
  onRequestClick,
  isLoadingPTOs = false,
  ptos = [],
  onEditRequestClick,
  lastPage,
  currentPage,
  totalPTOs,
  itemsPerPage,
  onPageChange,
  prefilledPTOType,
  editingRequest,
  isRequestedModalOpen,
  onCloseModal,
  onPTOsUpdate,
  adminMode = false,
  profile,
  isAdminEditOpen = false,
  onAdminEdit,
  onAdminEditClose,
  isBalanceLoading = false,
  onViewAnswerNoteClick,
  showAnswerViewerModal = false,
  onCloseAnswerViewerModal,
  answerViewerRequest,
}: Props): JSX.Element => {
  const tableHeaders = (
    <Tr>
      <Th>Personal Time Off Type</Th>
      <Th>Dates</Th>
      <Th>Total Hours</Th>
      <Th>Request Status</Th>
    </Tr>
  );

  const tableEmptyState = (
    <Tr>
      <Td colSpan={4} textAlign="center" padding="28px 74px" fontSize={14} lineHeight="150%">
        You’ve got no requested or scheduled time off.
      </Td>
    </Tr>
  );

  const renderPTORow = (data: PTORequest): JSX.Element => {
    const hours = (data.entries || []).reduce((sum, entry) => sum + entry.hours, 0);

    return (
      <Tr key={data.id}>
        <Td>
          <div>
            {iconByPTOType[data.ptoType]}
            <span>{ptoTypeLabel[data.ptoType]}</span>
          </div>
        </Td>
        <Td>
          {data.entries && data.entries.length > 0
            ? formatDateInterval(
                new Date(data.firstDay),
                new Date(data.entries[data.entries.length - 1].date),
                'MMM d'
              )
            : '-'}
        </Td>
        <Td>{`${hours} ${hours === 1 ? 'hour' : 'hours'}`}</Td>
        <Td>
          <div>
            <Styles.RequestStatus>
              {iconByPTOStatus[data.status]}
              <span>{PTOStatusLabel[data.status]}</span>
            </Styles.RequestStatus>
            {((!adminMode && data.status === PTOStatus.PENDING) ||
              (adminMode && data.status !== PTOStatus.PENDING)) && (
              <IconButton
                onClick={() => {
                  if (adminMode) {
                    onAdminEdit?.(data);
                  } else {
                    onEditRequestClick?.(data);
                  }
                }}
                icon={CreateIcon}
              />
            )}
            {adminMode && data.status === PTOStatus.PENDING && (
              <Styles.TinyButton
                size="small"
                variantStyle="outline"
                onClick={() => onAdminEdit?.(data)}
              >
                Answer
              </Styles.TinyButton>
            )}
            {!adminMode && data.status !== PTOStatus.PENDING && (
              <>
                {data.decisionComment ? (
                  <Styles.NoteButton onClick={() => onViewAnswerNoteClick?.(data)}>
                    <CommentIcon />
                    Note
                  </Styles.NoteButton>
                ) : (
                  <Styles.NoNote>No note</Styles.NoNote>
                )}
              </>
            )}
          </div>
        </Td>
      </Tr>
    );
  };

  const renderSkeletonRow = ({ id }: { id: string }): JSX.Element => (
    <Tr key={id}>
      <Td>
        <div className="tw-flex tw-h-[21px]">
          <Skeleton w={5} h={5} className="tw-mr-2.5" />
          <Skeleton h="14px" w="80px" className="tw-my-[3.5px]" />
        </div>
      </Td>
      <Td>
        <Skeleton h="14px" w="100px" className="tw-my-[3.5px]" />
      </Td>
      <Td>
        <div className="tw-flex !tw-justify-start">
          <Skeleton h="14px" w="12px" className="tw-my-[3.5px] tw-mr-1" />
          <Skeleton h="14px" w="60px" className="tw-my-[3.5px]" />
        </div>
      </Td>
      <Td>
        <div className="tw-flex !tw-justify-start">
          <SkeletonCircle w={2.5} h={2.5} className="tw-mr-2" />
          <Skeleton h="14px" w="80px" className="tw-my-[3.5px]" />
        </div>
      </Td>
    </Tr>
  );

  return (
    <>
      <div>
        <Styles.BalanceSlider
          isLoading={isBalanceLoading}
          balance={balance}
          onCardClick={!adminMode ? onRequestClick : undefined}
        />
        <Styles.RequestTimeoffContainer>
          <Styles.TableTitle>My Requested & Scheduled PTO</Styles.TableTitle>
          {!adminMode && (
            <Button size="medium" onClick={() => onRequestClick?.()}>
              Request PTO
            </Button>
          )}
        </Styles.RequestTimeoffContainer>
        <Styles.TableSubtitle>
          Please check your requests statuses and scheduled PTO below.
        </Styles.TableSubtitle>
        {isLoadingPTOs ? (
          <Styles.Table
            customBorderRadius={8}
            data={Array.from({ length: 10 }, (_, index) => ({ id: index })) as any[]}
            renderHeader={tableHeaders}
            renderRow={renderSkeletonRow}
          />
        ) : (
          <Styles.Table
            customBorderRadius={8}
            data={ptos}
            renderHeader={tableHeaders}
            renderEmptyState={tableEmptyState}
            renderRow={renderPTORow}
          />
        )}
        {lastPage !== undefined && lastPage > 1 && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            count={totalPTOs || 0}
            itemsPerPage={itemsPerPage}
            onChangePageHandler={onPageChange}
            hideItemsPerPageDropdown
          />
        )}
        <RequestTimeOffModal
          initialPTOType={prefilledPTOType}
          editing={editingRequest}
          isOpen={isRequestedModalOpen}
          onClose={onCloseModal}
          onTimeOffRequested={onPTOsUpdate}
          onTimeOffUpdated={onPTOsUpdate}
          onTimeOffDeleted={onPTOsUpdate}
        />
        {profile?.id && editingRequest && (
          <EditTimeoffRequestModal
            isOpen={isAdminEditOpen}
            request={editingRequest}
            onClose={onAdminEditClose}
            managerId={profile.id}
            onUpdateRequest={onPTOsUpdate}
          />
        )}
      </div>
      {answerViewerRequest?.decisionComment && (
        <PTOAnswerViewerModal
          isOpen={showAnswerViewerModal}
          onClose={onCloseAnswerViewerModal}
          answer={answerViewerRequest.decisionComment}
          manager={answerViewerRequest.answeredBy?.person}
        />
      )}
    </>
  );
};

export default MyTimeOffsPageContent;
